// 产品网格卡片组件 - 网格视图
// v4.3.0 - 性能优化：使用 React.memo 防止不必要的重渲染
import React, { memo, useCallback } from 'react';
import { Star, Image as ImageIcon } from 'lucide-react';
import type { Product } from '@/poster/types';
import { ProductCategoryLabels } from '@/poster/types';

interface ProductGridCardProps {
  product: Product;
  onViewDetail: (product: Product) => void;
}

// 使用 React.memo 包装，只在 props 真正变化时才重渲染
export const ProductGridCard = memo(function ProductGridCard({ product, onViewDetail }: ProductGridCardProps) {
  // 稳定化回调引用
  const handleViewDetail = useCallback(() => {
    onViewDetail(product);
  }, [onViewDetail, product]);
  const modelsCount = product.models?.length || 0;
  
  return (
    <div
      onClick={handleViewDetail}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-blue-200 transition-all cursor-pointer group"
    >
      {/* Image - 使用懒加载优化性能 */}
      <div className="aspect-[16/10] bg-gray-900 relative overflow-hidden">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-12 h-12 text-gray-600" />
          </div>
        )}
        
        {/* Featured Badge */}
        {product.is_featured && (
          <div className="absolute top-2 left-2">
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500 drop-shadow" />
          </div>
        )}
        
        {/* Category Badge */}
        <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium ${
          product.category === 'all_in_one' 
            ? 'bg-purple-500 text-white' 
            : product.category === 'module'
            ? 'bg-orange-500 text-white'
            : 'bg-blue-500 text-white'
        }`}>
          {ProductCategoryLabels[product.category]}
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <h3 className="font-semibold text-gray-900 truncate text-sm">{product.name}</h3>
        <p className="text-xs text-green-600 truncate">{product.series_name || '—'}</p>
        
        <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
          <span>{product.pitch_range || '—'}</span>
          <span className={modelsCount > 0 ? 'text-blue-600 font-medium' : 'text-gray-400'}>
            {modelsCount > 0 ? `${modelsCount} 型号` : '暂无型号'}
          </span>
        </div>
      </div>
    </div>
  );
});
